/*
 * COPYRIGHT (c) 2022
 * HONEYWELL INC.,
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { memo, useContext, useState } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../locales/en-US/descriptors';
import { Button } from '@scuf/common';
import ChartWidget from '../../../shared/Chart/ChartWidget';
import {
  convertDataForCarbonSavingsMeter,
  graphCarbon
} from '../../../shared/Utils/Utils';
import ProgressBarWidget from '../../../shared/ProgressBar/ProgressBarWidget';
import { ICarbonData } from '../../../DashboardKPIWidgets/IEnergyManagementProps';
import SavingsMeterWidget from '../../../shared/SavingsMeter/SavingsMeterWidget';
import { ApiContext } from '../../../../context/ApiContext';
import { ConversionType } from '../../../shared/Utils/unitHandling/UnitModel';
import { getProgressBarUnitScaledData } from '../../../shared/Utils/portfolioDataFormatter';
import { IGrapghData } from '../../../shared/Chart/IChartWidgetProps';
import { IDataTableHeader } from '../../../shared/BreakdownByMeter/BreakdownByMeterProps';
import { PERCENT_CHANGE_THRESHOLD } from '../../../shared/Utils/Constants';
interface ICarbonComponentProps {
  data: ICarbonData;
  toggleFeatureScopeOne: boolean | undefined;
}

export const CarbonComponentSite = (props: ICarbonComponentProps) => {
  const [t, lng] = useTranslation();
  const d = descriptors.energyManagement;
  const { data } = props;

  const apiContext = useContext(ApiContext);
  const { onCarbonViewDetailsClick } = apiContext;

  const onViewDetailsClick = () => {
    if (onCarbonViewDetailsClick) {
      onCarbonViewDetailsClick();
    }
  };

  const {
    emissionMeasurementUnit,
    totalAggregations: {
      totalActualEmission,
      totalPreviousPeriodEmission,
      totalDeviation,
      totalDeviationPercentage
    },
    monthWiseAggregations,
    emissionsByUtility,
    emissionsByScope
  } = data;
  const consumptionMeterData = props.toggleFeatureScopeOne
    ? convertDataForCarbonSavingsMeter(
        emissionsByScope,
        emissionMeasurementUnit,
        props.toggleFeatureScopeOne,
        apiContext.showChangeWhenData
      )
    : convertDataForCarbonSavingsMeter(
        emissionsByUtility,
        emissionMeasurementUnit,
        false,
        apiContext.showChangeWhenData
      );
  const { values, displayUnit } = consumptionMeterData;

  const [consumptionType, setconsumptionType] = useState<string>(
    t(d.HIGHWITHOUTUNIT)
  );

  const progressBarUnitScaledData = getProgressBarUnitScaledData(
    ConversionType.CARBON,
    {
      previousPeriod: totalPreviousPeriodEmission,
      actual: totalActualEmission
    },
    totalDeviation,
    emissionMeasurementUnit,
    undefined,
    null,
    null,
    undefined,
    apiContext.showChangeWhenData,
    true
  );

  const headers = [
    {
      HeaderValue: t(d.CARBONSAVINGSMETERHEADER),
      TextAlignValue: 'left',
      Field: 'ConsumptionTypeName'
    },
    {
      // HeaderValue: consumptionType,
      HeaderValue: t(d.CARBONNOUNIT),
      TextAlignValue: 'right',
      Field: 'ConsumptionHigh',
      sortable: true,
      initialWidth: '15%'
    },
    {
      HeaderValue: t(d.CHANGE),
      TextAlignValue: 'right',
      Field: 'ConsumptionDeviation',
      sortable: true,
      initialWidth: '25%'
    }
  ];

  const aggregationCarbonEmissionData = {
    originalActual: totalActualEmission,
    originalPreviousPeriod: totalPreviousPeriodEmission,
    originalSavings: totalDeviation,
    actual: progressBarUnitScaledData.actual,
    previousPeriod: progressBarUnitScaledData.previousPeriod,
    actualUnit: progressBarUnitScaledData.actualUnit,
    baselineUnit: progressBarUnitScaledData.baselineUnit,
    changeUnit: progressBarUnitScaledData.changeUnit,
    savings:
      progressBarUnitScaledData.deviation !== null &&
      Math.abs(Number(totalDeviationPercentage)) <= PERCENT_CHANGE_THRESHOLD
        ? progressBarUnitScaledData.deviation
        : apiContext.showChangeWhenData
        ? null
        : progressBarUnitScaledData.deviation,
    savingsPercentage:
      progressBarUnitScaledData.deviation === null
        ? null
        : totalDeviationPercentage
  };

  const graphData = graphCarbon(
    monthWiseAggregations,
    emissionMeasurementUnit,
    displayUnit,
    undefined,
    lng
  );

  const { YearIndex, MonthIndex } = graphData;

  let { 0: first, [MonthIndex.length - 1]: last } = MonthIndex;
  let { 0: previousyear, [YearIndex.length - 1]: currentyear } = YearIndex;

  const onSortingHandler = (sortOrder: number): void => {
    sortOrder === 1
      ? setconsumptionType(t(d.LOWWITHNOUNIT))
      : setconsumptionType(t(d.HIGHWITHOUTUNIT));
  };
  return (
    <div className="kpi-card-flex-container">
      <div className="kpi-card-progress-bar-container">
        <ProgressBarWidget
          unit={displayUnit}
          category="Energy"
          aggregationData={aggregationCarbonEmissionData}
          renderedFromCarbon={true}
          fromCarbonKPI={true}
          originalUnit={emissionMeasurementUnit}
          conversionType={ConversionType.CARBON}
        />
      </div>
      <div className="kpi-card-monthwise-container">
        {previousyear != currentyear ? (
          <div className="kpi-card-monthwise-date-range">
            {t(d.CARBONKPIDETAILS)} {first} {previousyear} - {last}{' '}
            {currentyear}
          </div>
        ) : (
          <div className="kpi-card-monthwise-date-range">
            {t(d.CARBONKPIDETAILS)} {first} - {last} {currentyear}
          </div>
        )}
        <ChartWidget
          graphData={graphData as IGrapghData}
          unit={displayUnit}
          axisUnitType="consumption"
          renderedFromCarbon={true}
        />
        <div className="kpi-card-view-details">
          <Button
            type="link"
            content={t(d.VIEWDETAILS)}
            className="view-details-button"
            onClick={() => onViewDetailsClick()}
          />
        </div>
      </div>
      <div className="kpi-card-meter-container">
        <SavingsMeterWidget
          columnHeaders={headers as IDataTableHeader[]}
          meterData={values}
          onSorting={onSortingHandler}
          toggleFeatureScopeOne={props.toggleFeatureScopeOne}
          emissionMeasurementUnit={emissionMeasurementUnit}
        />
      </div>
    </div>
  );
};
export default memo(CarbonComponentSite);
